import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowUpRight, Check, ChevronDown, CircleDashed, Copy, GitCommitHorizontal, History, MoreHorizontal, RotateCw, ScrollText, X } from "lucide-react";
import { useState } from "react";
import {
  ActorChip,
  Badge,
  describeStatus,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  RelativeTime,
  Spinner,
  toast,
  Tooltip,
} from "@/design";
import { useProjectSlug, useWorkspaceSlug } from "@/app";
import type { Deployment } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatDuration, shortSha } from "@/lib/format";
import { DeployActionDialog } from "./DeployActions";
import { compareUrl, failureLabel, isInFlight, triggerLabel, useLiveOperation } from "./deploy-utils";

/** Round status mark: tick, cross, spinner, history arrow. Always has a text alternative. */
export function DeployStatusIcon({ d, size = "md" }: { d: Pick<Deployment, "status" | "trigger">; size?: "sm" | "md" }) {
  const info = describeStatus("deployment", d.status);
  const rollback = d.trigger.kind === "rollback";
  const icon =
    d.status === "active" ? (
      rollback ? <History /> : <Check />
    ) : d.status === "failed" || d.status === "crashed" ? (
      <X />
    ) : isInFlight(d.status) ? (
      d.status === "waiting_approval" ? <CircleDashed /> : <Spinner className="size-3.5" />
    ) : rollback ? (
      <History />
    ) : (
      <span className="size-1.5 rounded-full bg-current" />
    );
  const tone = {
    ok: "bg-ok-soft text-ok-text border-ok/30",
    danger: "bg-danger-soft text-danger-text border-danger/30",
    info: "bg-info-soft text-info-text border-info/30",
    warn: "bg-warn-soft text-warn-text border-warn/35",
    idle: "bg-surface-2 text-subtle border-border",
    neutral: "bg-surface-2 text-subtle border-border",
    accent: "bg-accent-soft text-accent-text border-accent/25",
    ai: "bg-ai-soft text-ai-text border-ai/25",
  }[info.tone];
  return (
    <Tooltip content={`${info.label}. ${info.explain}`}>
      <span
        tabIndex={0}
        role="img"
        aria-label={info.label}
        className={cn("inline-flex shrink-0 items-center justify-center rounded-full border", size === "sm" ? "size-6 [&_svg]:size-3" : "size-7 [&_svg]:size-3.5", tone)}
      >
        {icon}
      </span>
    </Tooltip>
  );
}

const envTone = (env?: string) => (env === "production" ? "accent" : env?.startsWith("pr-") ? "info" : "warn");

export interface DeploymentRowProps {
  d: Deployment;
  /** The deploy that ran before this one for the same service (for "what changed"). */
  previous?: Deployment;
  /** Show service and environment (off inside a single-service panel). */
  showContext?: boolean;
  compact?: boolean;
}

export function DeploymentRow({ d, previous, showContext = true, compact }: DeploymentRowProps) {
  const workspace = useWorkspaceSlug();
  const project = useProjectSlug();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<null | "rollback" | "redeploy">(null);
  const [open, setOpen] = useState(false);
  const live = isInFlight(d.status);
  const op = useLiveOperation(d.operation_id, live);
  const compare = compareUrl(previous, d);
  const current = op.data?.steps?.find((s) => s.status === "running");
  const pct = op.data?.progress?.percent ?? 0;
  const canRollback = d.status === "superseded" && !!d.image;
  const canRedeploy = d.status === "active" || d.status === "superseded" || d.status === "crashed";
  const detail = { to: "/$workspace/$project/deployments/$deploymentId" as const, params: { workspace, project, deploymentId: d.id }, search: ((s: Record<string, unknown>) => s) as never };

  return (
    <li className={cn("group relative", compact ? "px-0 py-3" : "px-4 py-3.5 sm:px-5")}>
      <div className="flex gap-3">
        <div className="pt-0.5">
          <DeployStatusIcon d={d} size={compact ? "sm" : "md"} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <Link {...detail} className="block truncate text-base font-medium text-text hover:text-accent-text hover:underline hover:underline-offset-4">
                {d.source?.commit?.message ?? triggerLabel(d)}
              </Link>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted">
                {showContext && (
                  <>
                    <span className="font-medium text-text">{d.service_name}</span>
                    <Badge size="sm" variant="outline" tone={envTone(d.environment_name)}>
                      {d.environment_name}
                    </Badge>
                  </>
                )}
                {d.source?.commit?.sha && (
                  <span className="inline-flex items-center gap-1 font-mono text-xs">
                    <GitCommitHorizontal className="size-3.5 text-subtle" aria-hidden="true" />
                    {shortSha(d.source.commit.sha)}
                  </span>
                )}
                <span>{triggerLabel(d)}</span>
                {!compact && <ActorChip actor={d.trigger.actor} size="sm" />}
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1 text-sm">
              <RelativeTime value={d.created_at} className="text-muted" />
              {d.duration_ms != null && !live && <span className="tabular text-xs text-subtle" aria-label={`Took ${formatDuration(d.duration_ms)}`}>{formatDuration(d.duration_ms)}</span>}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label={`Actions for this deploy of ${d.service_name}`}
                className="-mr-1.5 -mt-0.5 flex size-7 items-center justify-center rounded-md text-subtle transition-colors hover:bg-surface-2 hover:text-text data-[state=open]:bg-surface-2"
              >
                <MoreHorizontal className="size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuItem icon={<ScrollText />} onSelect={() => void navigate(detail)}>
                  View details and log
                </DropdownMenuItem>
                {canRedeploy && (
                  <DropdownMenuItem icon={<RotateCw />} onSelect={() => setDialog("redeploy")}>
                    Redeploy this version
                  </DropdownMenuItem>
                )}
                {canRollback && (
                  <DropdownMenuItem icon={<History />} onSelect={() => setDialog("rollback")}>
                    Roll back to this version
                  </DropdownMenuItem>
                )}
                {compare && (
                  <DropdownMenuItem icon={<ArrowUpRight />} onSelect={() => window.open(compare, "_blank", "noopener")}>
                    Compare on GitHub
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  icon={<Copy />}
                  onSelect={() => {
                    void navigator.clipboard?.writeText(d.id).then(() => toast.info("Deploy ID copied", { description: d.id }));
                  }}
                >
                  Copy deploy ID
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {(d.status === "failed" || d.status === "crashed") && (
            <p className="mt-2 text-sm text-danger-text">
              <span className="font-medium">{d.failure?.category ? failureLabel[d.failure.category] : "Did not go live"}.</span>{" "}
              <span className="text-text/80">{d.failure?.message ?? d.status_text}</span>{" "}
              {d.status === "failed" && (
                <span className="text-muted">
                  {previous && (previous.status === "active" || previous.status === "superseded") ? "Your previous version kept serving." : "Nothing that was live changed."}
                </span>
              )}
            </p>
          )}
          {live && (
            <div className="mt-2.5 max-w-md">
              <div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted">
                <span className="truncate">{current?.title ? `${current.title}…` : (d.status_text ?? describeStatus("deployment", d.status).label)}</span>
                <span className="tabular">{pct}%</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-surface-3" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`Deploy of ${d.service_name} progress`}>
                <div className="h-full rounded-full bg-info transition-[width] duration-700" style={{ width: `${Math.max(4, pct)}%` }} />
              </div>
            </div>
          )}

          {!compact && (
            <>
              <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className="mt-2 inline-flex items-center gap-1 rounded text-xs font-medium text-muted hover:text-text"
              >
                <ChevronDown className={cn("size-3.5 transition-transform", open && "rotate-180")} aria-hidden="true" />
                What changed
              </button>
              {open && <ChangeSummary d={d} previous={previous} compare={compare} />}
            </>
          )}
        </div>
      </div>
      {dialog && <DeployActionDialog kind={dialog} deployment={d} open onOpenChange={(o) => !o && setDialog(null)} />}
    </li>
  );
}

/** Plain-English diff against the previous deploy: code, image and settings. */
export function ChangeSummary({ d, previous, compare }: { d: Deployment; previous?: Deployment; compare?: string | null }) {
  const sameImage = previous?.image?.digest && previous.image.digest === d.image?.digest;
  const configKeys = d.config && previous?.config ? Object.keys(d.config).filter((k) => JSON.stringify((d.config as Record<string, unknown>)[k]) !== JSON.stringify((previous.config as Record<string, unknown>)[k])) : [];
  return (
    <dl className="mt-2 grid gap-x-6 gap-y-2 rounded-lg border border-border bg-surface-2/60 px-3.5 py-3 text-sm sm:grid-cols-[8rem_1fr]">
      <dt className="text-muted">Code</dt>
      <dd className="min-w-0 text-text">
        {!previous ? (
          "First deploy of this service here."
        ) : previous.source?.commit?.sha === d.source?.commit?.sha ? (
          "Same commit as before."
        ) : (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <code className="font-mono text-xs">{shortSha(previous.source?.commit?.sha)}</code>
            <span aria-label="to">→</span>
            <code className="font-mono text-xs">{shortSha(d.source?.commit?.sha)}</code>
            {compare && (
              <a href={compare} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 font-medium text-accent-text hover:underline">
                See the diff on GitHub <ArrowUpRight className="size-3.5" aria-hidden="true" />
              </a>
            )}
          </span>
        )}
      </dd>
      <dt className="text-muted">Image</dt>
      <dd className="text-text">
        {d.trigger.kind === "rollback" ? "Reused the earlier image, so nothing was rebuilt." : sameImage ? "Same image, restarted with current settings." : d.image?.digest ? <>New image <code className="font-mono text-xs">{d.image.digest.slice(7, 19)}</code>{d.image.signed && " (signed)"}</> : "Not built yet."}
      </dd>
      <dt className="text-muted">Settings</dt>
      <dd className="text-text">{configKeys.length ? `Changed: ${configKeys.join(", ")}` : "No setting changes."}</dd>
    </dl>
  );
}
